'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  AUDIENCE_LABELS,
  AUDIENCE_TYPES,
  DURATION_BANDS,
  DURATION_BAND_LABELS,
  LEARNER_LEVELS,
  LEVEL_LABELS,
  RESOURCE_TYPES,
  RESOURCE_TYPE_LABELS,
  SKILL_LABELS,
  SKILL_TYPES,
} from '@vuganawe/types';
import { FormField, SelectField } from '../ui/fields';

const FILTERS = [
  { key: 'skill', label: 'Skill', values: SKILL_TYPES, labels: SKILL_LABELS },
  { key: 'level', label: 'Level', values: LEARNER_LEVELS, labels: LEVEL_LABELS },
  { key: 'duration', label: 'Time', values: DURATION_BANDS, labels: DURATION_BAND_LABELS },
  { key: 'audience', label: 'Audience', values: AUDIENCE_TYPES, labels: AUDIENCE_LABELS },
  { key: 'type', label: 'Type', values: RESOURCE_TYPES, labels: RESOURCE_TYPE_LABELS },
] as const;

/** URL-driven filter bar — filters live in the query string so they are shareable. */
export function ResourceFilterBar() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams.toString());
    if (value) params.set(key, value);
    else params.delete(key);
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  }

  const hasFilters = FILTERS.some((filter) => searchParams.get(filter.key)) || searchParams.get('search');

  return (
    <div className="vn-filterbar" role="search" aria-label="Filter resources">
      {FILTERS.map((filter) => (
        <SelectField
          key={filter.key}
          id={`filter-${filter.key}`}
          label={filter.label}
          placeholder="All"
          value={searchParams.get(filter.key) ?? ''}
          onChange={(event) => setParam(filter.key, event.target.value)}
          options={filter.values.map((value) => ({
            value,
            label: (filter.labels as Record<string, string>)[value] ?? value,
          }))}
        />
      ))}
      <FormField
        id="filter-search"
        label="Search"
        type="search"
        placeholder="e.g. interview"
        defaultValue={searchParams.get('search') ?? ''}
        onKeyDown={(event) => {
          if (event.key === 'Enter') setParam('search', event.currentTarget.value.trim());
        }}
        onBlur={(event) => setParam('search', event.currentTarget.value.trim())}
      />
      {hasFilters ? (
        <button
          type="button"
          className="vn-btn vn-btn--ghost vn-btn--small"
          onClick={() => router.replace(pathname, { scroll: false })}
        >
          Clear all
        </button>
      ) : null}
    </div>
  );
}
